export const AWS_PROD_ACCOUNT_ANNOTATION = 'aws.com/prod-account';
export const AWS_STAGING_ACCOUNT_ANNOTATION = 'aws.com/staging-account';
export const AZURE_PROD_SUBSCRIPTION_ANNOTATION = 'azure.com/prod-subscription';
export const AZURE_STAGING_SUBSCRIPTION_ANNOTATION =
  'azure.com/staging-subscription';

export type CloudIdentifierProvider = 'aws' | 'azure';

export interface CloudIdentifierInfo {
  id?: string;
  name?: string;
}

export interface CloudIdentifierChanges {
  prod?: CloudIdentifierInfo;
  staging?: CloudIdentifierInfo;
}

const AWS_ACCOUNT_ID_PATTERN = /^\d+$/;
export const AZURE_SUBSCRIPTION_ID_PATTERN =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

export function parseCloudIdentifierAnnotation(
  value: unknown,
): CloudIdentifierInfo {
  if (typeof value !== 'string' || !value.trim()) {
    return {};
  }
  try {
    const parsed: unknown = JSON.parse(value);
    if (!Array.isArray(parsed)) {
      return {};
    }
    const result: CloudIdentifierInfo = {};
    for (const item of parsed) {
      if (typeof item !== 'string') {
        continue;
      }
      const [key, ...valueParts] = item.split(':');
      const fieldValue = valueParts.join(':').trim();
      if (key === 'id' && fieldValue) {
        result.id = fieldValue;
      } else if (key === 'name' && fieldValue) {
        result.name = fieldValue;
      }
    }
    return result;
  } catch {
    return {};
  }
}

export function hasCloudIdentifierValue(
  value: CloudIdentifierInfo | undefined,
): boolean {
  return Boolean(value?.id?.trim() || value?.name?.trim());
}

function validateSide(
  provider: CloudIdentifierProvider,
  sideName: 'prod' | 'staging',
  value: unknown,
): string | undefined {
  if (!value || typeof value !== 'object' || Array.isArray(value)) {
    return `${provider}.${sideName} must be an object`;
  }
  const side = value as Record<string, unknown>;
  const unknownKeys = Object.keys(side).filter(
    key => key !== 'id' && key !== 'name',
  );
  if (unknownKeys.length > 0) {
    return `${provider}.${sideName} contains unsupported fields: ${unknownKeys.join(
      ', ',
    )}`;
  }
  if (
    side.id !== undefined &&
    side.id !== null &&
    typeof side.id !== 'string'
  ) {
    return `${provider}.${sideName}.id must be a string`;
  }
  if (
    side.name !== undefined &&
    side.name !== null &&
    typeof side.name !== 'string'
  ) {
    return `${provider}.${sideName}.name must be a string`;
  }
  const id = typeof side.id === 'string' ? side.id.trim() : '';
  const name = typeof side.name === 'string' ? side.name.trim() : '';
  if (Boolean(id) !== Boolean(name)) {
    return `${provider}.${sideName} requires both ID and Name, or both must be empty to remove it`;
  }
  if (!id) {
    return undefined;
  }
  if (provider === 'aws' && !AWS_ACCOUNT_ID_PATTERN.test(id)) {
    return `${provider}.${sideName}.id must contain numbers only`;
  }
  if (provider === 'azure' && !AZURE_SUBSCRIPTION_ID_PATTERN.test(id)) {
    return `${provider}.${sideName}.id must be a GUID in 8-4-4-4-12 format`;
  }
  if (name.length > 128 || [...name].some(char => char.codePointAt(0)! < 32)) {
    return `${provider}.${sideName}.name must be at most 128 characters and contain no control characters`;
  }
  return undefined;
}

export function validateCloudIdentifierChanges(
  value: unknown,
  provider: CloudIdentifierProvider,
): string | undefined {
  if (value === undefined) {
    return undefined;
  }
  if (!value || typeof value !== 'object' || Array.isArray(value)) {
    return `${provider} must be an object`;
  }
  const cloud = value as Record<string, unknown>;
  const unknownKeys = Object.keys(cloud).filter(
    key => key !== 'prod' && key !== 'staging',
  );
  if (unknownKeys.length > 0) {
    return `${provider} contains unsupported environments: ${unknownKeys.join(
      ', ',
    )}`;
  }
  for (const sideName of ['prod', 'staging'] as const) {
    if (!Object.hasOwn(cloud, sideName)) {
      continue;
    }
    const error = validateSide(provider, sideName, cloud[sideName]);
    if (error) {
      return error;
    }
  }
  return undefined;
}
